import { Suspense, useState } from "react";
import { Button, Icon } from "ui";
import { ArticleRow, Dialog, PriceDetailRow } from "@/components";
import { usePaymentLoadingStore } from "@/paymentLoadingStore";
import { queryClient } from "@/queryClient";
import { getOnSuccessUrl } from "@/utils";
import { formatCurrency } from "format-numbers";
import {
  useTranslation,
  Trans,
  initLocales,
  keycloakToMatchiLocale,
  keycloakToI18nLocale,
} from "i18n";
import { resources } from "@/locales";
import {
  checkoutResponse,
  getCheckoutQuery,
  getUserInfoQuery,
  userInfo,
} from "matchi-api";
import {
  Await,
  LoaderFunctionArgs,
  defer,
  useLoaderData,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { auth } from "matchi-auth";
import { getTokenOrLogin } from "@/utils/auth";
import { track, identifyUser, trackPageView } from "@/analytics";
import { setUser } from "@sentry/react";
import to from "await-to-js";

interface LoaderData {
  checkout: checkoutResponse;
  user: userInfo;
  onSuccessUrl: string;
}

const Page = () => {
  // TODO: These values should be inferred from loader data, but they are not.
  // @see https://github.com/remix-run/react-router/discussions/9792
  const { data } = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(false);

  return (
    <Suspense>
      <Await resolve={data}>
        {/* Data should be correctly inferred from useLoaderData, and we would not need this type */}
        {({ checkout, user, onSuccessUrl }: LoaderData) => {
          const locale = keycloakToMatchiLocale(auth.getLocale());
          const { t } = useTranslation();
          return (
            <>
              <div className="mb-6 space-y-2">
                <h1 className="text-lg font-medium">
                  {t("Thank you!")}
                </h1>
                <p className="text-gray-1200 leading-normal">
                  <Trans
                    i18nKey="success.emailConfirmation"
                    values={{ email: user.email }}
                    components={{
                      1: <span className="inline font-medium text-black" />,
                    }}
                  />
                </p>
              </div>
              <div className="border-default mb-4 overflow-hidden rounded-xl border">
                <header className="border-default flex items-center space-x-3 border-b bg-gray-200 px-4 py-3">
                  <div className="h-6 w-6 overflow-hidden rounded-sm">
                    <img
                      className="w-full rounded-sm"
                      src={checkout.facilityImageUrl}
                      alt="logo"
                    />
                  </div>
                  <h2 className="font-medium leading-none">
                    {checkout.facilityName}
                  </h2>
                </header>
                <ul>
                  {checkout.articles.map((article, index) => (
                    <li key={index}>
                      <ArticleRow
                        timeZone={checkout.facilityTimeZone!}
                        article={article}
                      />
                    </li>
                  ))}
                </ul>
                {!!checkout.adyenGiftCardOutcomes?.length &&
                  checkout.adyenGiftCardOutcomes.map((giftCard, index) => (
                    <SummaryOfferRow
                      key={index}
                      title={giftCard.name}
                      discountAmount={formatCurrency({
                        locale,
                        number: giftCard.amount,
                        currency: giftCard.currency!,
                      })}
                    />
                  ))}
                {checkout.promoCodeOutcomes.length > 0 &&
                  checkout.promoCodeOutcomes.map(promoCode => (
                    <SummaryOfferRow
                      key={promoCode.promoCode}
                      title={promoCode.promoCode}
                      discountAmount={formatCurrency({
                        locale,
                        number: parseFloat(promoCode.amount),
                        currency: checkout.price!.currency!,
                      })}
                    />
                  ))}
                {checkout.valueCardOutcomes.length > 0 &&
                  checkout.valueCardOutcomes.map(({ valueCard, amount }) => (
                    <SummaryOfferRow
                      key={valueCard.id}
                      title={valueCard.name}
                      discountAmount={formatCurrency({
                        locale,
                        number: amount,
                        currency: valueCard.currency,
                      })}
                    />
                  ))}
                <div className="flex justify-between p-4">
                  <span className="text-lg font-semibold">
                    {t("Total")}
                  </span>
                  <span className="text-theme-accent text-lg font-semibold">
                    {formatCurrency({
                      locale,
                      number: parseFloat(checkout.price?.amount!),
                      currency: checkout.price?.currency!,
                    })}
                  </span>
                </div>
                <div className="flex flex-1 bg-gray-200 p-4 border-t border-default">
                  <button
                    onClick={() => setPriceDetailsOpen(true)}
                    className="flex flex-1 justify-between items-center font-medium"
                  >
                    <span>
                      {t("Price details")}
                    </span>
                    <Icon icon="ChevronRightSolid" className="text-xl" />
                  </button>
                  <Dialog
                    open={priceDetailsOpen}
                    onClose={() => setPriceDetailsOpen(false)}
                    title={t("Price details")}
                  >
                    <ul>
                      {checkout.articles.map((article, index) => (
                        <li key={index}>
                          <PriceDetailRow article={article} />
                        </li>
                      ))}
                    </ul>
                  </Dialog>
                </div>
              </div>
              {onSuccessUrl && (
                <div className="mb-4">
                  {/*
                    An anchor rather than an onClick handler, for the same reason
                    as ReturnToAppButton: `onSuccessUrl` is `matchi://…` when the
                    app is the caller, and Chrome only hands a custom-scheme
                    intent to an app off the browser's own handling of a tap.
                  */}
                  <Button as="a" href={onSuccessUrl} size="lg" block>
                    {t("Go back")}
                  </Button>
                </div>
              )}
            </>
          );
        }}
      </Await>
    </Suspense>
  );
};

export const loader = async ({
  request,
  params: { token },
}: LoaderFunctionArgs) => {
  // We need to return a promise from the loader or Suspense will not wait for the data to be fetched.
  // @see https://reactrouter.com/en/main/guides/deferred#using-defer
  const fetchCheckoutData = async () => {
    if (!token) throw new Error("No token found");

    usePaymentLoadingStore.setState({
      isLoading: true,
      paymentFormSubmitting: false,
      verifyingRedirectResult: false,
    });

    // Get berarer token from keycloak
    const bearer = await getTokenOrLogin();
    const { locale: userLocale, sub: keycloakId } =
      jwtDecode<KeycloakJWTPayload>(bearer);

    // Identify users in Amplitude and Sentry
    identifyUser({ keycloakId });
    setUser({ id: keycloakId });

    // Track the page view only once the user is identified — Amplitude does not
    // backfill the User ID onto events that were already sent.
    // request.url, not window.location: React Router runs loaders to
    // completion *before* completeNavigation pushes the history entry, so on any
    // client-side navigation window.location still holds the previous URL. Both
    // routes into the success page are client-side (navigate() from
    // navigateToSuccess, redirect() from the action tail), so reading
    // window.location here reported the payment page's path and the dedupe then
    // correctly dropped it as a repeat — losing the funnel's terminal page view.
    trackPageView(new URL(request.url).pathname);

    // Initialize i18next
    const locale = keycloakToI18nLocale(userLocale);
    await initLocales({ locale, namespace: "checkout", resources });

    let err, user, checkout;

    [err, user] = await to(queryClient.ensureQueryData(getUserInfoQuery()));

    if (err || !user) throw new Error("Could not fetch user data");

    // The numeric MATCHi id is only known once the profile resolves. Set it as a
    // user property so staff can pivot from what the backend quotes to Amplitude.
    identifyUser({ keycloakId, matchiUserId: user.id });

    [err, checkout] = await to(
      queryClient.ensureQueryData(getCheckoutQuery(token)),
    );

    if (err || !checkout) throw new Error("Could not fetch checkout data");

    const onSuccessUrl = getOnSuccessUrl();

    return { user, checkout, onSuccessUrl };
  };

  const data = fetchCheckoutData()
    .catch(error => {
      track("Checkout Load Failed", {
        source: "checkout_success",
        // As in the payment loader: a non-Error rejection would otherwise send
        // reason: undefined on a field the catalogue declares required.
        reason: String(error?.message ?? error),
        checkout_token: token,
      });
      throw error;
    })
    .finally(() => {
      usePaymentLoadingStore.setState({
        isLoading: false,
        paymentFormSubmitting: false,
        verifyingRedirectResult: false,
      });
    });

  return defer({ data });
};

const SummaryOfferRow = ({
  title,
  discountAmount,
}: {
  title: string;
  discountAmount: string | number;
}) => {
  return (
    <div className="flex justify-between border-b border-default p-4">
      <span className="font-medium">{title}</span>
      <span className="font-medium">-{discountAmount}</span>
    </div>
  );
};

export const PaymentSuccess = { Page, loader };
